import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Search, ChevronRight, Heart, Star, MapPin, Coffee, UtensilsCrossed, Shirt, Sparkles, Gift, Calendar, Filter, SlidersHorizontal } from "lucide-react";

const formatDateUS = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

const categoryToMealType = (cat) => {
  const map = {
    "Restaurant": { label: "Lunch & Dinner", icon: UtensilsCrossed, order: 2 },
    "Fast Food": { label: "Quick Bites", icon: UtensilsCrossed, order: 3 },
    "Dessert & Ice Cream": { label: "Sweet Treats", icon: Sparkles, order: 4 },
    "Bakery": { label: "Morning Coffee & Bakery", icon: Coffee, order: 1 },
    "Cafes & Drinks": { label: "Morning Coffee & Bakery", icon: Coffee, order: 1 },
    "Clothing & Apparel": { label: "Shopping & Retail", icon: Shirt, order: 5 },
    "Retail": { label: "Shopping & Retail", icon: Shirt, order: 5 },
    "Beauty & Skincare": { label: "Beauty & Wellness", icon: Sparkles, order: 6 },
  };
  return map[cat] || { label: "Other Deals", icon: Gift, order: 7 };
};

const MiniLogo = ({ name }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <div className={`w-10 h-10 rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold text-sm">{name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
};

export const TimelineView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, stats, favoriteIds, toggleFavorite, searchQuery, setSearchQuery } = data;
  const [showFilters, setShowFilters] = useState(false);

  // Group offers by "meal type" / activity
  const grouped = {};
  offers.forEach(offer => {
    const { label, icon, order } = categoryToMealType(offer.category);
    if (!grouped[label]) grouped[label] = { label, icon, order, offers: [] };
    grouped[label].offers.push(offer);
  });
  const sections = Object.values(grouped).sort((a, b) => a.order - b.order);

  const totalValue = offers.reduce((sum, o) => sum + (o.offer_value_est || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Hero: Progress-style header */}
      <div className="bg-gradient-to-b from-sky-500 to-sky-400 px-6 pt-8 pb-6 text-white">
        <p className="text-sky-100 text-xs font-medium tracking-widest uppercase">Your Birthday Journey</p>
        <h1 className="text-3xl font-extrabold mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {stats.days_until_birthday} Days to Go
        </h1>
        <div className="mt-4 bg-white/20 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(5, 100 - (stats.days_until_birthday / 365 * 100))}%` }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="h-full bg-white rounded-full"
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-sky-100 text-sm">{offers.length} deals waiting</span>
          <span className="text-white font-bold text-lg">${totalValue.toFixed(0)} potential</span>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-6 -mt-4 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg shadow-sky-500/10 p-1 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400 ml-3" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search deals..."
            className="flex-1 py-3 text-sm outline-none"
            data-testid="timeline-search"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl transition-colors ${showFilters ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-500"}`}
            data-testid="timeline-filter-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter drawer */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pt-4 flex flex-wrap gap-2">
              {["Halal Only", "Exclude Boycott", "No App Needed", "Expiring Soon"].map(f => (
                <button key={f} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-500 hover:border-sky-300 hover:text-sky-600 transition-colors" data-testid={`timeline-filter-${f.toLowerCase().replace(/\s/g, '-')}`}>
                  {f}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Next Up — urgency highlight */}
      {expiringSoon.length > 0 && (
        <div className="px-6 mt-6">
          <div
            onClick={() => onOfferClick(expiringSoon[0])}
            className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100 cursor-pointer group hover:shadow-md transition-all"
            data-testid="timeline-next-up"
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-red-500" />
              <span className="text-xs font-bold text-red-500 tracking-wider uppercase">Next Up — Expiring!</span>
            </div>
            <div className="flex items-center gap-3">
              <MiniLogo name={expiringSoon[0].name} />
              <div className="flex-1">
                <p className="font-semibold text-slate-800">{expiringSoon[0].name}</p>
                {getStatus(expiringSoon[0].business_id) && <UnifiedStatusBadge status={getStatus(expiringSoon[0].business_id)} />}
                <p className="text-sm text-slate-500 truncate">{expiringSoon[0].offer_title}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">${expiringSoon[0].offer_value_est}</p>
                <ChevronRight className="w-4 h-4 text-slate-300 ml-auto group-hover:text-sky-500 transition-colors" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline sections by activity */}
      <div className="px-6 mt-6 relative">
        {/* Vertical line */}
        <div className="absolute left-[39px] top-0 bottom-0 w-0.5 bg-slate-200" />

        {sections.map((section, si) => (
          <div key={section.label} className="mb-8">
            {/* Section header */}
            <div className="flex items-center gap-3 mb-4 relative">
              <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center z-10 shadow-md shadow-sky-500/30">
                <section.icon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-base font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {section.label}
              </h2>
              <span className="text-xs text-slate-400 ml-auto">{section.offers.length} offers</span>
            </div>

            {/* Offers in this section */}
            <div className="ml-10 space-y-3">
              {section.offers.slice(0, 4).map((offer, i) => (
                <motion.div
                  key={offer.business_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: si * 0.05 + i * 0.03 }}
                  onClick={() => onOfferClick(offer)}
                  className="bg-white rounded-xl p-3.5 border border-slate-100 shadow-sm cursor-pointer hover:border-sky-200 hover:shadow-md transition-all flex items-center gap-3"
                  data-testid={`timeline-offer-${offer.business_id}`}
                >
                  <MiniLogo name={offer.name} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{offer.name}</p>
                    <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }}
                      className="p-0.5"
                    >
                      <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
                    </button>
                  </div>
                </motion.div>
              ))}
              {section.offers.length > 4 && (
                <button className="text-xs text-sky-500 font-medium ml-1" data-testid={`timeline-more-${section.label}`}>
                  + {section.offers.length - 4} more
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
